import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractApiErrorMessage } from '../../shared/utils/extract-api-error-message.util';
import { ADMIN_INVOICES_API_PATHS } from '../constants/invoices-app-paths.constant';
import { InvoiceFormat } from '../types/invoice-format.type';
import { InvoiceTemplateId } from '../types/invoice-template-id.type';

export type CreateSegmentInvoiceParams = {
  segmentId: string;
  templateId: InvoiceTemplateId;
  format: InvoiceFormat;
  from: string;
  to: string;
};

type CreateSegmentInvoiceResponse = {
  invoiceId: string;
  invoiceNumber: string;
  signedUrl: string;
};

export function useCreateSegmentInvoice() {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  const createInvoice = useCallback(
    async (params: CreateSegmentInvoiceParams): Promise<CreateSegmentInvoiceResponse | null> => {
      setIsLoading(true);
      try {
        const url = ADMIN_INVOICES_API_PATHS.CREATE_SEGMENT_INVOICE(params.segmentId);
        const response = await HttpClient.post<CreateSegmentInvoiceResponse>(url, {
          templateId: params.templateId,
          format: params.format,
          from: params.from,
          to: params.to,
        }, { timeout: 60000 });

        enqueueSnackbar(`Facture ${response.invoiceNumber} générée`, { variant: 'success' });
        window.open(response.signedUrl, '_blank', 'noopener,noreferrer');
        return response;
      } catch (error) {
        enqueueSnackbar(
          extractApiErrorMessage(error, 'Erreur lors de la génération de la facture'),
          { variant: 'error' },
        );
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  return { createInvoice, isLoading };
}
